/**
 * Settings sub-pages (Prompts 5 & 8):
 *   AudioCoachSettings        — /settings/audio
 *   NotificationsSettings     — /settings/notifications
 *   ConnectedDevicesSettings  — /settings/devices
 *   PrivacySettings           — /settings/privacy  (Nutritionist Access)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Volume2, Bell, Smartphone, ShieldCheck } from "lucide-react";
import { apiClient } from "../../services/api";
import useTtsCoach from "../../hooks/useTtsCoach";
import { toast } from "sonner";

function SettingsScreen({ title, children, testid }) {
  const navigate = useNavigate();
  return (
    <div className="app-page" data-testid={testid}>
      <header className="sticky top-0 z-20 bg-canvas border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" className="-ml-2 w-10 h-10 grid place-items-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-display text-[20px]">{title}</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 pt-4 pb-32 space-y-4">{children}</main>
    </div>
  );
}

// ─── Audio Coach ──────────────────────────────────────────────
export function AudioCoachSettings() {
  const { available, voice, setVoice, enabled, setEnabled, speak } = useTtsCoach();

  const voices = [
    { id: "calm_male", label: "Calm Male" },
    { id: "energetic_male", label: "Energetic Male" },
    { id: "calm_female", label: "Calm Female" },
  ];

  return (
    <SettingsScreen title="Audio Coach" testid="audio-coach-settings">
      <div className="bg-white rounded-[16px] p-5 border border-line2">
        <div className="flex items-center gap-3">
          <Volume2 size={22} />
          <div className="flex-1">
            <div className="font-medium text-[15px]">Voice coaching</div>
            <div className="text-[12px] text-ink-soft">Plays during workout sessions.</div>
          </div>
          <Switch
            checked={enabled}
            onChange={(v) => {
              if (v && !available) {
                toast.error("Audio coach not configured. Add ELEVENLABS_API_KEY in backend .env");
                return;
              }
              setEnabled(v);
            }}
            testid="audio-coach-toggle"
          />
        </div>
        {!available && (
          <div className="mt-3 text-[11px] text-amber-700">
            Audio coach is currently unavailable. Provide an ElevenLabs API key in the backend
            .env to enable.
          </div>
        )}
      </div>

      <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="voice-picker">
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-2">Voice</div>
        <div className="space-y-2">
          {voices.map((v) => {
            const on = voice === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setVoice(v.id)}
                data-testid={`voice-${v.id}`}
                className="w-full flex items-center justify-between px-4 h-12 rounded-[12px] border border-line2"
                style={{
                  background: on ? "var(--accent-blue-dim)" : "transparent",
                  borderColor: on ? "var(--accent-blue)" : "var(--border)",
                }}
              >
                <span className="text-[14px] font-medium">{v.label}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    speak("Welcome to FitFrik. Let us get started.");
                  }}
                  className="text-[11px] underline"
                >
                  Preview
                </button>
              </button>
            );
          })}
        </div>
      </div>
    </SettingsScreen>
  );
}

// ─── Notifications ──────────────────────────────────────────────
export function NotificationsSettings() {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    apiClient.get("/notifications/prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({}));
  }, []);

  const update = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await apiClient.put("/notifications/prefs", next);
    } catch {
      toast.error("Could not update");
    }
  };

  if (!prefs) {
    return (
      <SettingsScreen title="Notifications" testid="notifs-settings">
        <div className="text-center py-10 text-ink-soft text-[13px]">Loading…</div>
      </SettingsScreen>
    );
  }

  return (
    <SettingsScreen title="Notifications" testid="notifs-settings">
      <div className="bg-white rounded-[16px] divide-y divide-line2 border border-line2 overflow-hidden">
        <Toggle
          label="Workout reminder"
          desc="Daily nudge at your chosen time"
          checked={!!prefs.workout_reminder_enabled}
          onChange={(v) => update({ workout_reminder_enabled: v })}
          testid="toggle-workout-reminder"
        />
        {prefs.workout_reminder_enabled && (
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-ink-tertiary mb-1.5">Time</div>
            <input
              type="time"
              value={prefs.workout_reminder_time || "07:00"}
              onChange={(e) => update({ workout_reminder_time: e.target.value })}
              data-testid="reminder-time"
              className="w-full h-11 px-3 rounded-[12px] border border-line bg-canvas2"
            />
          </div>
        )}
        <Toggle
          label="Streak at risk"
          desc="Alert if no workout/meal logged by 8 PM"
          checked={!!prefs.streak_at_risk_enabled}
          onChange={(v) => update({ streak_at_risk_enabled: v })}
          testid="toggle-streak"
        />
        <Toggle
          label="Community interactions"
          desc="When someone likes/comments your post"
          checked={!!prefs.community_interactions_enabled}
          onChange={(v) => update({ community_interactions_enabled: v })}
          testid="toggle-community"
        />
        <Toggle
          label="Weekly summary"
          desc="Mondays at 9 AM"
          checked={!!prefs.weekly_summary_enabled}
          onChange={(v) => update({ weekly_summary_enabled: v })}
          testid="toggle-summary"
        />
      </div>
      <p className="text-[11px] text-ink-tertiary text-center px-2">
        Notifications respect quiet hours: 10 PM – 7 AM in your local timezone.
      </p>
    </SettingsScreen>
  );
}

// ─── Connected Devices ──────────────────────────────────────────
export function ConnectedDevicesSettings() {
  const [data, setData] = useState({ devices: [], sleep_preference: null });

  const refresh = () =>
    apiClient.get("/wearables/devices").then((r) => setData(r.data || { devices: [], sleep_preference: null })).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const connect = async (provider) => {
    try {
      const { data: res } = await apiClient.post("/wearables/devices/connect", { provider });
      if (res.first_time_notice) {
        toast(
          "Device connected. Where both device and manual data exist we will use the higher value automatically for steps and calories. For sleep you can choose your preference.",
          { duration: 8000 }
        );
      } else {
        toast.success("Device connected");
      }
      refresh();
    } catch {
      toast.error("Could not connect");
    }
  };

  const disconnect = async (provider) => {
    try {
      await apiClient.post("/wearables/devices/disconnect", { provider });
      toast.success("Disconnected");
      refresh();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  const setSleepPref = async (decision) => {
    try {
      await apiClient.post("/wearables/sleep-preference", { decision });
      toast.success("Sleep preference saved");
      refresh();
    } catch {
      toast.error("Could not save");
    }
  };

  const isConnected = (p) => data.devices.some((d) => d.provider === p);
  const providers = [
    { id: "apple_health", label: "Apple Health (iOS)" },
    { id: "health_connect", label: "Google Health Connect" },
    { id: "fitbit", label: "Fitbit" },
    { id: "garmin", label: "Garmin" },
  ];

  return (
    <SettingsScreen title="Connected Devices" testid="devices-settings">
      <div className="bg-white rounded-[16px] divide-y divide-line2 border border-line2 overflow-hidden">
        {providers.map((p) => {
          const connected = isConnected(p.id);
          return (
            <div key={p.id} className="px-4 py-4 flex items-center justify-between" data-testid={`provider-${p.id}`}>
              <div className="flex items-center gap-3">
                <Smartphone size={18} />
                <div>
                  <div className="text-[14px] font-medium">{p.label}</div>
                  <div className="text-[11px] text-ink-soft">{connected ? "Connected" : "Not connected"}</div>
                </div>
              </div>
              {connected ? (
                <button
                  onClick={() => disconnect(p.id)}
                  data-testid={`disconnect-${p.id}`}
                  className="px-3 h-9 rounded-pill text-[12px] border border-line2"
                  style={{ borderRadius: 100 }}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => connect(p.id)}
                  data-testid={`connect-${p.id}`}
                  className="px-3 h-9 rounded-pill text-[12px]"
                  style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
                >
                  Connect
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-white rounded-[16px] p-5 border border-line2" data-testid="sleep-pref">
        <div className="text-[14px] font-medium">Sleep data preference</div>
        <p className="text-[12px] text-ink-soft mt-1">
          When device & manual sleep entries differ, which should we use?
        </p>
        <div className="flex gap-2 mt-3">
          {[
            { id: "use_device", label: "Device" },
            { id: "keep_manual", label: "My entry" },
          ].map((o) => {
            const on = data.sleep_preference === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setSleepPref(o.id)}
                data-testid={`sleep-pref-${o.id}`}
                className="flex-1 h-10 rounded-pill text-[13px]"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  fontWeight: on ? 700 : 600,
                  borderRadius: 100,
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-ink-tertiary text-center px-2">
        Wearables sync is currently MOCKED — native bridge required for real HealthKit / Health Connect integration.
      </p>
    </SettingsScreen>
  );
}

// ─── Privacy / Nutritionist Access ──────────────────────────────
export function PrivacySettings() {
  const [data, setData] = useState({ pending: [], active_grants: [] });

  const refresh = () =>
    apiClient.get("/nutrition/access/list").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const decide = async (request_id, decision) => {
    try {
      await apiClient.post("/nutrition/access/decide", { request_id, decision });
      toast.success(decision === "allow" ? "Access granted" : "Request declined");
      refresh();
    } catch {
      toast.error("Could not respond");
    }
  };

  const revoke = async (nutritionist_user_id) => {
    try {
      await apiClient.post("/nutrition/access/revoke", { member_user_id: nutritionist_user_id });
      toast.success("Access revoked");
      refresh();
    } catch {
      toast.error("Could not revoke");
    }
  };

  return (
    <SettingsScreen title="Privacy" testid="privacy-settings">
      <section data-testid="pending-requests">
        <h2 className="font-display text-[18px] mb-2">Pending requests</h2>
        {data.pending.length === 0 && (
          <div className="text-[13px] text-ink-soft">No pending requests.</div>
        )}
        {data.pending.map((r) => (
          <div key={r.request_id} className="bg-white rounded-[12px] p-4 border border-line2 mb-2">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-cyan-500" />
              <span className="font-medium text-[14px]">{r.nutritionist_name}</span>
            </div>
            <p className="text-[12px] text-ink-soft mt-1">
              Requesting to view your food log to provide guidance.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => decide(r.request_id, "allow")}
                data-testid={`allow-${r.request_id}`}
                className="flex-1 h-10 rounded-pill"
                style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
              >
                Allow
              </button>
              <button
                onClick={() => decide(r.request_id, "decline")}
                data-testid={`decline-${r.request_id}`}
                className="flex-1 h-10 rounded-pill border border-line2"
                style={{ borderRadius: 100 }}
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </section>
      <section data-testid="active-grants" className="mt-6">
        <h2 className="font-display text-[18px] mb-2">Nutritionists with access</h2>
        {data.active_grants.length === 0 && (
          <div className="text-[13px] text-ink-soft">No active grants.</div>
        )}
        {data.active_grants.map((g) => (
          <div key={g.nutritionist_user_id} className="bg-white rounded-[12px] p-3 border border-line2 mb-2 flex items-center justify-between">
            <div>
              <div className="text-[14px] font-medium">{g.nutritionist_user_id}</div>
              <div className="text-[11px] text-ink-soft">Granted {(g.granted_at || "").slice(0, 10)}</div>
            </div>
            <button
              onClick={() => revoke(g.nutritionist_user_id)}
              data-testid={`revoke-${g.nutritionist_user_id}`}
              className="text-[12px] text-red-500"
            >
              Revoke
            </button>
          </div>
        ))}
      </section>
    </SettingsScreen>
  );
}

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between px-4 py-4">
      <div className="flex-1">
        <div className="text-[14px] font-medium">{label}</div>
        <div className="text-[12px] text-ink-soft">{desc}</div>
      </div>
      <Switch checked={checked} onChange={onChange} testid={testid} />
    </div>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testid}
      aria-pressed={checked}
      className="w-11 h-6 rounded-full transition-colors relative"
      style={{ background: checked ? "#00D4FF" : "#C7C4BF" }}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform"
        style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}
